import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import router from 'next/router'

import { AppThunk } from '@/store'

export type OverlayView =
  | { type: 'CLOSED' }
  | { type: 'LOGIN' }
  | { type: 'LOGOUT' }
  | { type: 'PRODUCT'; productId: string }
  | { type: 'UNAVAILABLE' }
  | { type: 'FEATURE_FLAGS' }

type OverlayActionEvent = 'redirect' | 'view'

export interface OverlayState {
  originalPath: string
  view: OverlayView
  onSuccess: {
    event: OverlayActionEvent
    redirectPath?: string
    view?: OverlayView
  } | null
  onClose: {
    event: OverlayActionEvent
    redirectPath?: string
  } | null
}

export const closeOverlay: AppThunk = (dispatch, getState) => {
  const onClose = getState().overlay?.onClose

  if (onClose?.event === 'redirect' && onClose.redirectPath) {
    router.push(onClose.redirectPath)
    dispatch(resetOverlayState())
    return
  }
  dispatch(resetOverlayState())
}

export const onOverlaySuccess: AppThunk = (dispatch, getState) => {
  const onSuccess = getState().overlay?.onSuccess

  if (onSuccess?.event === 'redirect' && onSuccess.redirectPath) {
    router.push(onSuccess.redirectPath)
    dispatch(resetOverlayState())
    return
  }

  if (onSuccess?.event === 'view' && onSuccess.view) {
    dispatch(setOverlayView({ view: onSuccess.view }))
    return
  }
  dispatch(resetOverlayState())
}

const initialState: OverlayState = {
  originalPath: '',
  view: { type: 'CLOSED' },
  onClose: null,
  onSuccess: null,
}

export const overlaySlice = createSlice({
  name: 'overlay',
  initialState,
  reducers: {
    setOverlayView: (
      state: OverlayState,
      action: PayloadAction<{
        view: OverlayState['view']
        onClose?: OverlayState['onClose']
        onSuccess?: OverlayState['onSuccess']
      }>,
    ) => {
      state.view = action.payload.view
      state.onClose = action.payload.onClose ?? null
      state.onSuccess = action.payload.onSuccess ?? null
    },
    setOverlayPath: (state: OverlayState, action: PayloadAction<{ originalPath: string }>) => {
      state.originalPath = action.payload.originalPath
    },
    resetOverlayState: () => initialState,
  },
})

export const { setOverlayPath, setOverlayView, resetOverlayState } = overlaySlice.actions

export default overlaySlice.reducer
